import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Step,
    StepLabel,
    Stepper,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import BuildIcon from '@material-ui/icons/Build'

import { useOrganizationCompanyList } from '../../hooks/useOrganizationCompanyList'
import { useSignableDocCatalog } from '../../hooks/Outreach/useSignableDocs'
import { useUser } from '../../hooks/useUser'
import {
    useApplyOrgConfigBundle,
    useOrgApplicationWorkbooks,
} from '../ApplicationWorkbook'
import { useOrgLeadsPipelineConfigs } from './useOrgPipelineConfigApi'
import { formatOrgPipelineMenuLabel } from '../LeasingSeason/Configurations/PipelineBuilder/pipelineConfigProvenance'
import { usePipelineConfigTemplates } from '../PipelineBuilder'
import type { ConfigBundleApplyResult } from '../ApplicationWorkbook/useApplicationWorkbookApi'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    headerRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
    },
    propertyList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
        maxHeight: 280,
        overflowY: 'auto',
        marginTop: theme.spacing(1),
    },
    resultList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    resultRow: {
        padding: theme.spacing(1.5),
        borderRadius: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
    },
    note: {
        color: theme.palette.text.secondary,
        fontSize: '0.8125rem',
        marginTop: theme.spacing(1),
    },
    field: {
        width: '100%',
        marginTop: theme.spacing(2),
    },
}))

const STEPS = ['Properties', 'Pipeline & workbook', 'Review']

function formatPropertyResult(
    propertyName: string,
    row: ConfigBundleApplyResult['properties'][number],
): string {
    const parts: string[] = []
    if (row.error) {
        return `${propertyName}: ${row.error}`
    }
    if (row.pipeline) {
        parts.push(
            row.pipeline.status === 'created'
                ? `Pipeline created (${row.pipeline.name})`
                : row.pipeline.status === 'updated'
                ? `Pipeline updated (${row.pipeline.name})`
                : `Pipeline ${row.pipeline.status}`,
        )
    }
    if (row.workbook) {
        parts.push(`Workbook ${row.workbook.status}`)
    }
    if (row.signable_docs?.length) {
        parts.push(
            `Signable docs: ${row.signable_docs
                .map((doc) => `${doc.template_key} (${doc.status})`)
                .join(', ')}`,
        )
    }
    return `${propertyName}: ${parts.join('; ') || 'No changes'}`
}

export const PropertyConfigBundleWizard: React.FC = () => {
    const classes = useStyles()
    const { organization } = useUser()
    const organizationId = organization

    const apartmentsQuery = useOrganizationCompanyList(
        organizationId,
        'APARTMENT',
    )
    const apartments = apartmentsQuery.data ?? []
    const orgPipelineConfigsQuery = useOrgLeadsPipelineConfigs(organizationId)
    const orgPipelineConfigs = orgPipelineConfigsQuery.data ?? []
    const orgWorkbooksQuery = useOrgApplicationWorkbooks(organizationId)
    const orgWorkbooks = orgWorkbooksQuery.data ?? []
    const templatesQuery = usePipelineConfigTemplates()
    const templates = templatesQuery.data ?? []
    const catalogQuery = useSignableDocCatalog({ pipeline_eligible: true })
    const catalogEntries =
        catalogQuery.data?.filter((entry) => entry.available) ?? []
    const bundleMutation = useApplyOrgConfigBundle(organizationId)

    const [open, setOpen] = useState(false)
    const [activeStep, setActiveStep] = useState(0)
    const [selectedPropertyIds, setSelectedPropertyIds] = useState<number[]>([])
    const [orgPipelineConfigId, setOrgPipelineConfigId] = useState<number | ''>(
        '',
    )
    const [orgWorkbookId, setOrgWorkbookId] = useState<number | ''>('')
    const [includePipeline, setIncludePipeline] = useState(true)
    const [includeWorkbook, setIncludeWorkbook] = useState(true)
    const [includeSignableDocs, setIncludeSignableDocs] = useState(false)
    const [catalogTemplateKeys, setCatalogTemplateKeys] = useState<string[]>([])
    const [
        applyResult,
        setApplyResult,
    ] = useState<ConfigBundleApplyResult | null>(null)

    const templatesByKey = useMemo(
        () => new Map(templates.map((template) => [template.key, template])),
        [templates],
    )

    const apartmentNameById = useMemo(
        () => new Map(apartments.map((apt) => [apt.id, apt.name])),
        [apartments],
    )

    useEffect(() => {
        if (open) {
            setActiveStep(0)
            setSelectedPropertyIds(apartments.map((apt) => apt.id))
            setOrgPipelineConfigId(
                orgPipelineConfigs.length > 0 ? orgPipelineConfigs[0].id : '',
            )
            setOrgWorkbookId(orgWorkbooks.length > 0 ? orgWorkbooks[0].id : '')
            setIncludePipeline(orgPipelineConfigs.length > 0)
            setIncludeWorkbook(orgWorkbooks.length > 0)
            setIncludeSignableDocs(catalogEntries.length > 0)
            setCatalogTemplateKeys(
                catalogEntries[0] ? [catalogEntries[0].template_key] : [],
            )
            setApplyResult(null)
        }
    }, [open, apartments, orgPipelineConfigs, orgWorkbooks, catalogEntries])

    const toggleProperty = (propertyId: number) => {
        setSelectedPropertyIds((current) =>
            current.includes(propertyId)
                ? current.filter((id) => id !== propertyId)
                : [...current, propertyId],
        )
    }

    const canAdvance = useMemo(() => {
        if (activeStep === 0) {
            return selectedPropertyIds.length > 0
        }
        if (activeStep === 1) {
            if (!includePipeline && !includeWorkbook && !includeSignableDocs) {
                return false
            }
            if (includePipeline && orgPipelineConfigId === '') return false
            if (includeWorkbook && orgWorkbookId === '') return false
            if (includeSignableDocs && catalogTemplateKeys.length === 0)
                return false
            return true
        }
        return true
    }, [
        activeStep,
        includePipeline,
        includeWorkbook,
        includeSignableDocs,
        catalogTemplateKeys.length,
        orgWorkbookId,
        orgPipelineConfigId,
        selectedPropertyIds.length,
    ])

    const handleApply = async () => {
        const payload: {
            property_ids: number[]
            org_workbook_id?: number
            org_pipeline_config_id?: number
            catalog_template_keys?: string[]
        } = {
            property_ids: selectedPropertyIds,
        }

        if (includePipeline && orgPipelineConfigId !== '') {
            payload.org_pipeline_config_id = orgPipelineConfigId
        }
        if (includeWorkbook && orgWorkbookId !== '') {
            payload.org_workbook_id = orgWorkbookId
        }
        if (includeSignableDocs && catalogTemplateKeys.length > 0) {
            payload.catalog_template_keys = catalogTemplateKeys
        }

        const result = await bundleMutation.mutateAsync(payload)
        setApplyResult(result)
        setActiveStep(2)
    }

    const closeWizard = () => {
        setOpen(false)
    }

    const isLoading =
        apartmentsQuery.isLoading ||
        orgPipelineConfigsQuery.isLoading ||
        orgWorkbooksQuery.isLoading ||
        catalogQuery.isLoading

    return (
        <Box className={classes.root}>
            <Box className={classes.headerRow}>
                <Typography variant="body2" color="textSecondary">
                    Set up pipeline, workbook, and signable doc configs on new
                    properties before their first leasing season.
                </Typography>
                <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<BuildIcon />}
                    onClick={() => setOpen(true)}
                    disabled={isLoading || apartments.length === 0}
                >
                    Set up properties
                </Button>
            </Box>

            <Dialog open={open} onClose={closeWizard} maxWidth="sm" fullWidth>
                <DialogTitle>Set up property configurations</DialogTitle>
                <DialogContent>
                    <Stepper activeStep={activeStep} alternativeLabel>
                        {STEPS.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    {activeStep === 0 && (
                        <Box mt={2}>
                            <Typography variant="body2">
                                Select properties to configure.
                            </Typography>
                            <Box className={classes.propertyList}>
                                {apartments.map((apt) => (
                                    <FormControlLabel
                                        key={apt.id}
                                        control={
                                            <Checkbox
                                                checked={selectedPropertyIds.includes(
                                                    apt.id,
                                                )}
                                                onChange={() =>
                                                    toggleProperty(apt.id)
                                                }
                                                color="primary"
                                            />
                                        }
                                        label={apt.name}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}

                    {activeStep === 1 && (
                        <Box mt={2}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={includePipeline}
                                        onChange={(event) =>
                                            setIncludePipeline(
                                                event.target.checked,
                                            )
                                        }
                                        color="primary"
                                    />
                                }
                                label="Apply org pipeline to each property"
                            />
                            {includePipeline && (
                                <FormControl
                                    variant="outlined"
                                    size="small"
                                    className={classes.field}
                                >
                                    <InputLabel id="bundle-pipeline-label">
                                        Org pipeline
                                    </InputLabel>
                                    <Select
                                        labelId="bundle-pipeline-label"
                                        label="Org pipeline"
                                        value={orgPipelineConfigId}
                                        onChange={(event) =>
                                            setOrgPipelineConfigId(
                                                Number(event.target.value),
                                            )
                                        }
                                    >
                                        {orgPipelineConfigs.map((config) => (
                                            <MenuItem
                                                key={config.id}
                                                value={config.id}
                                            >
                                                {formatOrgPipelineMenuLabel(
                                                    config,
                                                    templatesByKey,
                                                )}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={includeWorkbook}
                                        onChange={(event) =>
                                            setIncludeWorkbook(
                                                event.target.checked,
                                            )
                                        }
                                        color="primary"
                                    />
                                }
                                label="Apply org workbook to each property"
                            />
                            {includeWorkbook && (
                                <FormControl
                                    variant="outlined"
                                    size="small"
                                    className={classes.field}
                                >
                                    <InputLabel id="bundle-workbook-label">
                                        Org workbook
                                    </InputLabel>
                                    <Select
                                        labelId="bundle-workbook-label"
                                        label="Org workbook"
                                        value={orgWorkbookId}
                                        onChange={(event) =>
                                            setOrgWorkbookId(
                                                Number(event.target.value),
                                            )
                                        }
                                    >
                                        {orgWorkbooks.map((workbook) => (
                                            <MenuItem
                                                key={workbook.id}
                                                value={workbook.id}
                                            >
                                                {workbook.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={includeSignableDocs}
                                        onChange={(event) =>
                                            setIncludeSignableDocs(
                                                event.target.checked,
                                            )
                                        }
                                        color="primary"
                                    />
                                }
                                label="Provision signable docs from catalog"
                            />
                            {includeSignableDocs && (
                                <FormControl
                                    variant="outlined"
                                    size="small"
                                    className={classes.field}
                                >
                                    <InputLabel id="bundle-signable-docs-label">
                                        Catalog templates
                                    </InputLabel>
                                    <Select
                                        labelId="bundle-signable-docs-label"
                                        label="Catalog templates"
                                        multiple
                                        value={catalogTemplateKeys}
                                        onChange={(event) =>
                                            setCatalogTemplateKeys(
                                                event.target.value as string[],
                                            )
                                        }
                                        renderValue={(selected) =>
                                            (selected as string[]).join(', ')
                                        }
                                    >
                                        {catalogEntries.map((entry) => (
                                            <MenuItem
                                                key={entry.template_key}
                                                value={entry.template_key}
                                            >
                                                <Checkbox
                                                    checked={catalogTemplateKeys.includes(
                                                        entry.template_key,
                                                    )}
                                                    color="primary"
                                                />
                                                {entry.display_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            <Typography className={classes.note}>
                                Signable docs provision property-level DocuSeal
                                templates from the EZOS catalog. Pipeline refs
                                resolve automatically when an org pipeline is
                                included.
                            </Typography>
                        </Box>
                    )}

                    {activeStep === 2 && applyResult && (
                        <Box mt={2}>
                            <Typography variant="body2" gutterBottom>
                                Pipeline created: {applyResult.pipeline_created}
                                {applyResult.pipeline_updated != null &&
                                    applyResult.pipeline_updated > 0 &&
                                    `, updated: ${applyResult.pipeline_updated}`}
                                {applyResult.pipeline_skipped_locked != null &&
                                    applyResult.pipeline_skipped_locked > 0 &&
                                    ` (${applyResult.pipeline_skipped_locked} locked)`}
                                {applyResult.pipeline_errors > 0 &&
                                    ` (${applyResult.pipeline_errors} errors)`}
                                {' · '}
                                Workbook created: {applyResult.workbook_created}
                                , updated: {applyResult.workbook_updated}
                                {applyResult.workbook_skipped_locked > 0 &&
                                    ` (${applyResult.workbook_skipped_locked} locked)`}
                                {' · '}
                                Signable docs:{' '}
                                {applyResult.signable_docs_created}
                                {applyResult.signable_docs_errors > 0 &&
                                    ` (${applyResult.signable_docs_errors} errors)`}
                            </Typography>
                            <Box className={classes.resultList}>
                                {applyResult.properties.map((row) => (
                                    <Box
                                        key={row.property_id}
                                        className={classes.resultRow}
                                    >
                                        <Typography variant="body2">
                                            {formatPropertyResult(
                                                apartmentNameById.get(
                                                    row.property_id,
                                                ) ??
                                                    `Property ${row.property_id}`,
                                                row,
                                            )}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeWizard}>Close</Button>
                    {activeStep > 0 && activeStep < 2 && (
                        <Button
                            onClick={() => setActiveStep((step) => step - 1)}
                        >
                            Back
                        </Button>
                    )}
                    {activeStep < 1 && (
                        <Button
                            color="primary"
                            variant="contained"
                            disabled={!canAdvance}
                            onClick={() => setActiveStep((step) => step + 1)}
                        >
                            Next
                        </Button>
                    )}
                    {activeStep === 1 && (
                        <Button
                            color="primary"
                            variant="contained"
                            disabled={!canAdvance || bundleMutation.isLoading}
                            onClick={handleApply}
                        >
                            {bundleMutation.isLoading ? (
                                <CircularProgress size={22} />
                            ) : (
                                'Apply bundle'
                            )}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    )
}
